/* eslint-disable @typescript-eslint/no-explicit-any */

import {ConversationContext} from '@stripe-internal/conversations-client';
import {
  AnalyticsEventIdMessage,
  MessageFromParent,
  MessageFromParentType,
  VisibilityChangeMessage,
  DocumentVisibilityChangeMessage,
  RequestActionResponseMessage,
  GetSmartSuggestionsMessage,
  ContextHandoff,
} from '../Providers/ParentMessageBusProvider/MessageFromParent';
import {Visibility} from '../Providers/ParentMessageBusProvider/Visibility';
import {devLog} from './dev';

export function readMessageFromParent(message: any): MessageFromParent | null {
  // Explicitly making new objects to ensure we don't include any data outside
  // of our message types.

  if (isVisibilityChange(message)) {
    return {
      type: message.type,
      data: {
        visibility: message.data.visibility,
      },
      audience: message.audience,
      entryPoint: message.entryPoint,
    };
  } else if (isDocumentVisibilityChange(message)) {
    return {
      type: message.type,
      data: {
        documentVisibility: message.data.documentVisibility,
      },
      audience: message.audience,
      entryPoint: message.entryPoint,
    };
  } else if (isAnalyticsEventId(message)) {
    return {
      type: message.type,
      data: {
        analyticsEventId: message.data.analyticsEventId,
      },
      audience: message.audience,
      entryPoint: message.entryPoint,
    };
  } else if (isRequestActionResponse(message)) {
    return {
      type: message.type,
      data: message.data,
      nonce: message.nonce,
      audience: message.audience,
      entryPoint: message.entryPoint,
    };
  } else if (isGetSuggestedQueries(message)) {
    return {
      type: message.type,
      audience: message.audience,
      entryPoint: message.entryPoint,
    };
  } else if (isContextHandoff(message)) {
    return {
      type: message.type,
      context: message.context as ConversationContext,
    };
  } else {
    if (!isReactDevTools(message)) {
      devLog('Unknown message received from parent', {message});
    }
    return null;
  }
}

function isReactDevTools(message: any) {
  return (
    typeof message.source === 'string' &&
    message.source.includes('react-devtools')
  );
}

function isVisibilityChange(message: any): message is VisibilityChangeMessage {
  return (
    checkObjectKeys(message, ['type', 'data']) &&
    message.type === MessageFromParentType.VisibilityChange &&
    checkObjectKeys(message.data, ['visibility']) &&
    Object.values<string>(Visibility).includes(message.data.visibility)
  );
}

function isDocumentVisibilityChange(
  message: any,
): message is DocumentVisibilityChangeMessage {
  return (
    checkObjectKeys(message, ['type', 'data']) &&
    message.type === MessageFromParentType.DocumentVisibilityChange &&
    checkObjectKeys(message.data, ['documentVisibility']) &&
    typeof message.data.documentVisibility === 'string'
  );
}

function isAnalyticsEventId(message: any): message is AnalyticsEventIdMessage {
  return (
    checkObjectKeys(message, ['type', 'data']) &&
    message.type === MessageFromParentType.AnalyticsEventId &&
    checkObjectKeys(message.data, ['analyticsEventId']) &&
    typeof message.data.analyticsEventId === 'string'
  );
}

function isRequestActionResponse(
  message: any,
): message is RequestActionResponseMessage {
  return (
    checkObjectKeys(message, ['type', 'data', 'nonce']) &&
    message.type === MessageFromParentType.RequestActionResponse &&
    typeof message.nonce === 'string'
  );
}

function isGetSuggestedQueries(
  message: any,
): message is GetSmartSuggestionsMessage {
  return (
    checkObjectKeys(message, ['type']) &&
    message.type === MessageFromParentType.GetSmartSuggestions
  );
}

function isContextHandoff(message: any): message is ContextHandoff {
  return (
    checkObjectKeys(message, ['type', 'context']) &&
    message.type === MessageFromParentType.ContextHandoff
  );
}

function checkObjectKeys(obj: any, expectedKeys: string[]): boolean {
  const actualKeys = Object.keys(obj).filter(
    // we don't want to include these optional filters in the comparison
    (k) => !['audience', 'entryPoint'].includes(k),
  );
  return (
    actualKeys.length === expectedKeys.length &&
    expectedKeys.every((k) => actualKeys.includes(k))
  );
}
